import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Alert
} from '@mui/material';
import { LogoutOutlined, Dashboard as DashboardIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Leads from './Leads';
import Webinars from './Webinars';
import RevenueDashboard from './RevenueDashboard';
import RecurringIssuesDashboard from './RecurringIssuesDashboard';
import AdminSettings from './AdminSettings';
import PlanActivation from './PlanActivation';
import PaymentLinks from './PaymentLinks';
import Team from './Team';
import Requests from './Requests';
import SupportSearch from './SupportSearch';
import ErrorBoundary from '../common/ErrorBoundary';
import { useAuth } from '../../context/AuthContext';
import { SalesProvider, useSales } from '../../context/SalesContext';
import { CssBaseline, ThemeProvider } from '@mui/material';
import salesTheme from '../../theme/salesTheme';
import { Tag } from './leads/ui';

// Tabs in display order. `show` decides who sees each one.
const TABS = [
  { key: 'search', label: 'Search', show: (s) => s.isSupport, render: () => <SupportSearch /> },
  { key: 'leads', label: 'Leads', show: (s) => s.can('leads'), render: () => <Leads /> },
  { key: 'webinars', label: 'Webinars', show: (s) => s.can('webinar_leads'), render: () => <Webinars /> },
  { key: 'payment-links', label: 'Payment Links', show: (s) => s.can('payment_links'), render: () => <PaymentLinks /> },
  { key: 'revenue', label: 'Revenue', show: (s) => s.can('revenue'), render: () => <RevenueDashboard /> },
  { key: 'team', label: 'Team', show: (s) => s.isManager, render: () => <Team /> },
  { key: 'requests', label: 'Requests', show: (s) => s.isManager || s.isSupport, render: () => <Requests /> },
  { key: 'recurring', label: 'Recurring Issues', show: (s) => s.isAdmin, render: () => <RecurringIssuesDashboard /> },
  { key: 'plan-activation', label: 'Plan Activation', show: (s) => s.isAdmin, render: () => <PlanActivation /> },
  { key: 'admin', label: 'Admin Settings', show: (s) => s.isAdmin, render: () => <AdminSettings /> },
];

// ViewSwitch: a leader moves between the whole team and their personal leads (and can
// narrow to one executive); an admin narrows to a team or a person. Every screen follows.
const ViewSwitch = () => {
  const sales = useSales();
  const { view, setView, team } = sales;
  if (!sales.isManager) return null;

  const active = team.filter((m) => m.active);
  const leaders = active.filter((m) => m.leader);
  const people = sales.isAdmin && view.team ? active.filter((m) => m.owner === view.team || m.parent_owner === view.team) : active;

  const selectSx = { minWidth: 124, '& .MuiSelect-select': { py: '6.5px', fontWeight: 500 } };
  const selectProps = { displayEmpty: true };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      {sales.isLeader && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view.me ? 'me' : 'team'}
          onChange={(_, v) => v && setView({ me: v === 'me', member: '', team: '' })}
        >
          <ToggleButton value="team" sx={{ px: 1.5 }}>Team</ToggleButton>
          <ToggleButton value="me" sx={{ px: 1.5 }}>My leads</ToggleButton>
        </ToggleButtonGroup>
      )}
      {sales.isAdmin && leaders.length > 0 && (
        <TextField
          select
          size="small"
          value={view.team}
          onChange={(e) => setView({ me: false, member: '', team: e.target.value })}
          sx={selectSx}
          SelectProps={selectProps}
        >
          <MenuItem value="">All teams</MenuItem>
          {leaders.map((m) => <MenuItem key={m.owner} value={m.owner}>{m.owner}'s team</MenuItem>)}
        </TextField>
      )}
      {!view.me && people.length > 1 && (
        <TextField
          select
          size="small"
          value={view.member}
          onChange={(e) => setView({ ...view, me: false, member: e.target.value })}
          sx={selectSx}
          SelectProps={selectProps}
        >
          <MenuItem value="">Everyone</MenuItem>
          {people.map((m) => <MenuItem key={m.owner} value={m.owner}>{m.owner}{m.leader ? ' (leader)' : ''}</MenuItem>)}
        </TextField>
      )}
    </Box>
  );
};

const roleLabel = { admin: 'Admin', leader: 'Team leader', executive: 'Executive', support: 'Support' };

const DashboardShell = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const sales = useSales();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const loginTime = localStorage.getItem('loginTime');
    const minutes = Math.floor((new Date() - new Date(loginTime)) / 60000);
    if (!token || !(minutes < 60 * 24 * 20)) {
      navigate('/');
    }
  }, [navigate]);

  const handleLogout = () => {
    logout();
    ['token', 'number', 'loginTime'].forEach((k) => localStorage.removeItem(k));
    navigate('/');
  };

  const tabs = useMemo(() => (sales.loading ? [] : TABS.filter((t) => t.show(sales))), [sales]);
  const wanted = searchParams.get('tab');
  const active = tabs.find((t) => t.key === wanted) || tabs[0];

  const selectTab = (key) => {
    const next = new URLSearchParams();
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      {/* Top bar: who you are, what you are looking at, logout */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', position: 'sticky', top: 0, zIndex: 20 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pt: 1.25, pb: 0.75, flexWrap: 'wrap' }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #3558E0 0%, #7048D8 100%)', color: '#fff', fontWeight: 800, fontSize: 15, flexShrink: 0
            }}
            >
              R
            </Box>
            <Box sx={{ lineHeight: 1.15, mr: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: '-0.01em' }}>Returns Sales</Typography>
              {sales.name && <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.2 }}>{sales.name}</Typography>}
            </Box>
            {sales.role && roleLabel[sales.role] && (
              <Tag tone={sales.isAdmin ? 'dark' : sales.isLeader ? 'primary' : sales.isSupport ? 'purple' : 'neutral'}>{roleLabel[sales.role]}</Tag>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <ViewSwitch />
            {/* The coupons dashboard is a sales tool: not for support users. */}
            {!sales.isSupport && (
              <Tooltip title="Admin dashboard (coupons)">
                <IconButton size="small" onClick={() => navigate('/dashboard')} sx={{ color: 'text.secondary' }}>
                  <DashboardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Log out">
              <IconButton size="small" onClick={handleLogout} sx={{ color: 'text.secondary' }}>
                <LogoutOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {tabs.length > 0 && (
            <Tabs
              value={active ? active.key : false}
              onChange={(_, key) => selectTab(key)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ ml: -1.5, '& .MuiTabs-scrollButtons.Mui-disabled': { width: 0 } }}
            >
              {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
            </Tabs>
          )}
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: { xs: 1.5, sm: 2.5 }, mb: 4, px: { xs: 1, sm: 3 } }}>
        {sales.loading && <Box sx={{ p: 3 }}>Loading...</Box>}
        {!sales.loading && sales.role === 'unauthorized' && (
          <Alert severity="warning">
            This number does not have access to the sales dashboard. Ask your team leader to add you.
          </Alert>
        )}
        {!sales.loading && sales.role !== 'unauthorized' && !active && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="body1" color="text.secondary">Your team leader has not given you access to any section yet.</Typography>
          </Paper>
        )}
        {active && (
          // Remount on view change so every screen refetches for the new slice.
          <ErrorBoundary resetKey={`${active.key}|${sales.viewKey}`}>
            <Box key={`${active.key}|${sales.viewKey}`}>{active.render()}</Box>
          </ErrorBoundary>
        )}
      </Container>
    </Box>
  );
};

const SalesDashboard = () => (
  <ThemeProvider theme={salesTheme}>
    <CssBaseline />
    <SalesProvider>
      <DashboardShell />
    </SalesProvider>
  </ThemeProvider>
);

export default SalesDashboard;
